import { CARDS, cardBySlug, cardsInSuit, relatedCards, SITE, suitOf } from '../data.js';
import { LD } from '../head.js';
import { mountPage } from '../mount.js';
import { cardFace, clip, esc, icon, startCta } from '../ui.js';

// Optional shared card renderer (deal agent). When present, the hero card becomes the same
// canvas face used in the 3D table; the CSS face stays as the prerendered fallback.
const cardFaceModules = import.meta.glob('../../lib/cardFace.js');

/** @param {import('../data.js').Card} card */
const suitLabel = (card) => (card.suit === 'unicorn' ? '' : `${suitOf(card.suit).name} `);

/** @param {import('../data.js').Card} card */
export function cardTitle(card) {
  return `${card.fullName}: the Fair Play ${suitLabel(card)}card explained`;
}

/** @param {import('../data.js').Card} card */
export function cardDescription(card) {
  const tags = [`${suitOf(card.suit).name} suit`, card.dailyGrind ? 'Daily Grind' : ''].filter(
    Boolean,
  );
  const head = `The ${card.fullName} card (${tags.join(', ')}). `;
  const def = (card.definition ?? card.conception ?? '').split('\n')[0];
  return head + clip(def, 158 - head.length);
}

/** @param {string | null} text */
const paras = (text) =>
  (text ?? '')
    .split(/\n{2,}/)
    .filter(Boolean)
    .map((p) => `<p class="mk-body">${esc(p)}</p>`)
    .join('');

/** @param {{t: string, text: string}[]} blocks */
const blocks = (blocks) => {
  if (!blocks.length) return '';
  const lis = blocks.filter((b) => b.t === 'li');
  const ps = blocks.filter((b) => b.t !== 'li');
  return (
    ps.map((b) => `<p>${esc(b.text)}</p>`).join('') +
    (lis.length ? `<ul>${lis.map((b) => `<li>${esc(b.text)}</li>`).join('')}</ul>` : '')
  );
};

/** @param {import('../data.js').Card} card */
function badges(card) {
  const out = [
    `<span class="mk-badge mk-badge--${card.suit}">${icon(card.suit, 'mk-inline-ico')} ${suitOf(card.suit).name} suit</span>`,
  ];
  if (card.dailyGrind)
    out.push(
      `<span class="mk-badge mk-badge--dg">${icon('cup', 'mk-inline-ico')} Daily Grind</span>`,
    );
  if (card.happinessTrio)
    out.push(
      `<span class="mk-badge mk-badge--ht">${icon('smile', 'mk-inline-ico')} Happiness Trio</span>`,
    );
  if (card.perPlayer)
    out.push(`<span class="mk-badge">One per player · Player ${card.playerSlot}</span>`);
  if (card.kids) out.push('<span class="mk-badge">Kids</span>');
  return out.join('');
}

/** Contextual advice for holding this kind of card, drawn from the method. */
/** @param {import('../data.js').Card} card */
function tips(card) {
  const t = [];
  if (card.suit === 'wild') {
    t.push(
      'Whoever is dealt this card can ask for more help, guilt-free.',
      'Their partner picks up other cards and reaches out to the wider village.',
      'Set non-essential cards aside temporarily, and re-deal at your next check-in.',
    );
  } else if (card.suit === 'unicorn') {
    t.push(
      'Both partners hold their own Unicorn Space card. Always.',
      'Name it, set a six-month goal, and commit weekly hours.',
      'Protect your partner’s time as fiercely as your own.',
    );
  } else {
    if (card.perPlayer)
      t.push('This card is dealt to each of you. Nobody holds it for their partner.');
    if (card.dailyGrind)
      t.push(
        'It’s a Daily Grind: relentless and time-bound. Make sure each of you holds a fair share of these, ideally one from each suit.',
      );
    t.push(
      'The holder owns all of it: noticing, planning and doing. No “just tell me what to do.”',
      'Agree the Minimum Standard of Care together, then let the holder meet it their way.',
      'Not working? Bring it to the weekly check-in and re-deal it. Don’t take it back mid-week.',
    );
  }
  return t.map((x) => `<li>${icon('check', 'mk-inline-ico')}<span>${x}</span></li>`).join('');
}

/** @param {import('../data.js').Card} card */
function cpe(card) {
  if (card.suit === 'wild') {
    return `<section class="mk-card-sec" data-reveal>
      <h2 class="mk-h3">How Wild cards work</h2>
      ${paras(card.conception)}
    </section>`;
  }
  return `<section class="mk-card-sec" data-reveal>
    <h2 class="mk-h3">Holding this card means owning all three</h2>
    <div class="mk-cpe-trio mk-cpe-trio--card">
      <div><span class="mk-cpe-letter">C</span><h3>Conception</h3><p>${esc(card.conception)}</p></div>
      <div><span class="mk-cpe-letter">P</span><h3>Planning</h3>${blocks(card.planning) || '<p class="muted">Agree the plan together.</p>'}</div>
      <div><span class="mk-cpe-letter">E</span><h3>Execution</h3>${blocks(card.execution) || '<p class="muted">Do it to the standard you agreed.</p>'}</div>
    </div>
  </section>`;
}

/** @param {import('../data.js').Card} card */
function msc(card) {
  if (card.suit === 'unicorn') {
    return `<section class="mk-card-sec mk-msc-sec" data-reveal>
      <p class="mk-kicker">The card asks you</p>
      <p class="mk-msc">What makes me feel at least two of: exhilarated, content, fulfilled, or focused? My Unicorn Space for the next six months will be ____. I will commit __ hours a week to pursue it.</p>
    </section>`;
  }
  if (!card.msc) return '';
  return `<section class="mk-card-sec mk-msc-sec" data-reveal>
    <p class="mk-kicker">Suggested Minimum Standard of Care</p>
    <p class="mk-msc">${esc(card.msc)}</p>
    <p class="mk-note">A starting point. You and your partner agree the real standard together, and the holder meets it without reminders.</p>
  </section>`;
}

/** @param {import('../data.js').Card} card */
function body(card) {
  const suit = suitOf(card.suit);
  const inSuit = cardsInSuit(card.suit);
  const i = inSuit.findIndex((c) => c.slug === card.slug);
  const prev = inSuit[(i - 1 + inSuit.length) % inSuit.length];
  const next = inSuit[(i + 1) % inSuit.length];
  const related = relatedCards(card)
    .map((c) => `<li>${cardFace(c, { tag: 'a', href: `/cards/${c.slug}` })}</li>`)
    .join('');
  const resources = card.resources.length
    ? `<section class="mk-card-sec" data-reveal><h2 class="mk-h3">Further reading</h2><ul class="mk-resources">${card.resources
        .map((r) => `<li><a href="${esc(r.url)}" rel="noopener nofollow">${esc(r.title)}</a></li>`)
        .join('')}</ul></section>`
    : '';
  return `<div class="mk-wrap mk-card-page mk-card-page--${card.suit}">
  <nav class="mk-crumbs" aria-label="Breadcrumb"><a href="/cards">Cards</a><span aria-hidden="true">/</span><a href="/cards?suit=${suit.id}">${suit.name}</a><span aria-hidden="true">/</span><span aria-current="page">${esc(card.fullName)}</span></nav>
  <div class="mk-card-layout">
    <div class="mk-card-visual">
      <div class="mk-card-visual-in" data-card-visual>${cardFace(card, { cls: 'mk-big-card' })}</div>
      <div class="mk-card-pager">
        <a href="/cards/${prev.slug}" rel="prev"><span>${icon('arrow', 'mk-inline-ico mk-flip')}</span> ${esc(prev.fullName)}</a>
        <a href="/cards/${next.slug}" rel="next">${esc(next.fullName)} <span>${icon('arrow', 'mk-inline-ico')}</span></a>
      </div>
    </div>
    <article class="mk-card-text">
      <header class="mk-card-header">
        <p class="mk-eyebrow">A card from <cite>Fair Play</cite> by Eve Rodsky</p>
        <h1 class="mk-display mk-display--md">${esc(card.fullName)}</h1>
        <div class="mk-badges">${badges(card)}</div>
      </header>
      <section class="mk-card-sec mk-card-def">${paras(card.definition)}</section>
      ${cpe(card)}
      ${msc(card)}
      <section class="mk-card-sec" data-reveal>
        <h2 class="mk-h3">Holding it well</h2>
        <ul class="mk-tips">${tips(card)}</ul>
      </section>
      ${resources}
      <div class="mk-card-cta" data-reveal>
        <div><h2 class="mk-h3">Put this card on your table</h2><p>Deal it, agree its standard, and see who holds what. Free.</p></div>
        ${startCta('Start your deck')}
      </div>
    </article>
  </div>
  <section class="mk-related" data-reveal>
    <h2 class="mk-h3">Related cards</h2>
    <ul class="mk-card-grid mk-card-grid--related">${related}</ul>
    <p><a class="mk-link" href="/cards">All 100 cards ${icon('arrow', 'mk-link-ico')}</a></p>
  </section>
</div>`;
}

/** @param {{ slug?: string }} params @returns {import('../head.js').PageView} */
export function view(params) {
  const card = cardBySlug(params.slug ?? '');
  if (!card) {
    return {
      path: `/cards/${params.slug ?? ''}`,
      title: 'Card not found · Fair Play Deck',
      description: 'That card isn’t in the deck.',
      body: `<section class="mk-page-hero"><div class="mk-wrap"><h1 class="mk-display mk-display--md">That card isn’t in the deck.</h1><p class="mk-lede"><a href="/cards">Browse all 100 cards</a>.</p></div></section>`,
    };
  }
  const path = `/cards/${card.slug}`;
  const twin =
    card.playerSlot === 2
      ? CARDS.find((c) => c.pairKey === card.pairKey && c.playerSlot === 1)
      : null;
  const ogSuit = card.suit;
  return {
    path,
    canonicalPath: twin ? `/cards/${twin.slug}` : path,
    title: cardTitle(card),
    description: cardDescription(card),
    body: body(card),
    ogType: 'article',
    ogImage: `/og/og-${ogSuit}.png`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'DefinedTerm',
        name: card.fullName,
        description: clip(card.definition ?? card.conception ?? '', 300),
        url: SITE.origin + path,
        termCode: card.slug,
        inDefinedTermSet: {
          '@type': 'DefinedTermSet',
          name: 'The Fair Play deck',
          url: `${SITE.origin}/cards`,
        },
        isBasedOn: LD.book,
      },
      LD.breadcrumbs([
        ['Cards', '/cards'],
        [suitOf(card.suit).name, `/cards?suit=${card.suit}`],
        [card.fullName, path],
      ]),
    ],
  };
}

/** Swap in the shared canvas face if the deal agent's renderer exists. */
function upgradeVisual(root, slug) {
  const loader = cardFaceModules['../../lib/cardFace.js'];
  const host = root.querySelector('[data-card-visual]');
  const card = cardBySlug(slug);
  if (!loader || !host || !card) return;
  let alive = true;
  loader()
    .then((mod) => {
      const m = /** @type {any} */ (mod);
      if (!alive || typeof m.renderCardElement !== 'function') return;
      const size = Math.round(
        Math.min(260, host.clientWidth * (host.clientWidth > 400 ? 0.8 : 0.58)),
      );
      const el = m.renderCardElement(card, { size });
      if (!(el instanceof HTMLElement)) return;
      el.classList.add('mk-big-card', 'mk-canvas-card');
      host.replaceChildren(el);
    })
    .catch(() => {});
  return () => {
    alive = false;
  };
}

/** @param {HTMLElement} el @param {{ slug?: string }} params */
export function render(el, params) {
  return mountPage(el, view(params), (root) => upgradeVisual(root, params.slug ?? ''));
}
